// Csapat-lista panel (org-structure „Csapatok" fül) — a KÖZÖS DataTable-re épül. A szervezeti fa KÖZÉPSŐ
// szintje: a csapat egy részleghez tartozik, és beosztásokat tart. Szerver-oldali szabad-szöveg kereső
// (300 ms debounce, név/slug), szerver-rendezés (name | slug | color | departmentName), végtelen görgetés
// (100/lap). A /org-structure/teams BFF-végpontról fogyaszt (workforce.teams busz-erőforrás; a részleg-név
// feloldva: departmentId → departmentName).
//
// A SZÍN ITT él (a részlegnek nincs): a csapat-badge a rendszer egyetlen szervezeti szín-forrása — ugyanez
// a szín jelenik meg a munkatársak avatarján, a beosztás-soron és az eszköz-listán.
//
// Page-gate: `organizational_structure.view`. CRUD: „+ Új" + ceruza (organizational_structure.manage →
// TeamFormSheet) + kuka (organizational_structure.delete). Törlés-guard: a szerver 409-et ad, ha a csapathoz
// beosztás VAGY eszköz tartozik → a conflict-ág visszaállít + a „használatban" toastot mutatja.
import { type OrgTeamRowSchema } from '@atlas/contracts';
import { ExclamationTriangleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { OrgStructureTabs, type OrgTab } from '@/components/org-structure/org-structure-tabs';
import { TeamFormSheet } from '@/components/org-structure/team-form-sheet';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { TeamBadge } from '@/components/shared/team-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ExpandableCell } from '@/components/shared/expandable-cell';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { DeleteRowAction, EditRowAction, RowActions } from '@/components/shared/list-row-actions';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonChip } from '@/components/shared/person-chip';
import { departmentKeys, positionKeys, teamKeys } from '@/features/org-structure/api';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { api } from '@/lib/api';
import { formatName } from '@/lib/format-name';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

type TeamRow = z.infer<typeof OrgTeamRowSchema>;
type Member = TeamRow['members'][number];
type ListPage = Awaited<ReturnType<typeof api.orgTeams.query>>;

const PAGE_SIZE = 100;

/** A részleg-facet „mind" pszeudó-értéke (mint az employees/accounts oldalon). */
const ALL = '__all__';

export function TeamsPanel({
  tab,
  onTabChange,
  deepLinkEditId,
  onClearDeepLink,
}: {
  tab: OrgTab;
  onTabChange: (value: string) => void;
  /** Deep-link: a `?edit=` paraméterből érkező csapat-id (a szerkesztő-sheet erre nyílik, ha a sor betöltött). */
  deepLinkEditId?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.toLowerCase().startsWith('en');
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('organizational_structure.view');
  const canManage = can('organizational_structure.manage');
  const canDelete = can('organizational_structure.delete');

  const [formTarget, setFormTarget] = useState<{ team: TeamRow | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TeamRow | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>(ALL);
  const [sort, setSort] = useState<string>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const timer = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const gated = !permsLoading && !canView;
  const filters = { q, sort, order };
  const listKey = teamKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.orgTeams.query({
        query: { q: q || undefined, sort, order, limit: PAGE_SIZE, offset: pageParam },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(() => pages.flatMap((p) => (p.status === 200 ? p.body.teams : [])), [pages]);

  // Deep-link: a `?edit=<id>` csapatra nyitjuk a szerkesztő-sheetet, amint a sor betöltött (különben graceful no-op).
  useDeepLinkSheet<TeamRow>({
    id: deepLinkEditId,
    rows,
    getId: (tm) => tm.id,
    onOpen: (tm) => setFormTarget({ team: tm }),
  });

  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));

  // Részleg-facet: opciók a betöltött sorok feloldott részleg-neveiből (distinct, magyar collation). A részlegre
  // NEM a keresőben, hanem EZZEL a szűrővel lehet szűkíteni (kliens-oldal — a belső csapat-szám kicsi, egy lap).
  const collator = useMemo(() => new Intl.Collator('hu', { sensitivity: 'base' }), []);
  const departmentOptions = useMemo(
    () =>
      [...new Set(rows.map((r) => r.departmentName).filter((v): v is string => !!v))].sort((a, b) =>
        collator.compare(a, b),
      ),
    [rows, collator],
  );
  const filteredRows = useMemo(
    () =>
      departmentFilter === ALL ? rows : rows.filter((r) => r.departmentName === departmentFilter),
    [rows, departmentFilter],
  );
  const hasFilters = q !== '' || departmentFilter !== ALL;

  const toggleSort = useCallback((col: string) => {
    setSort((prev) => {
      if (prev === col) {
        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setOrder('asc');
      return col;
    });
  }, []);
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista sor-eltávolítás + rollback). A REFERENCIA-GUARD 409-et ad, ha a csapathoz beosztás
  // VAGY eszköz tartozik — a conflict-ág visszaállít (rollback) + a „használatban" toastot mutatja (nincs
  // végleges törlés).
  const removeMutation = useEntityMutation<{ id: string; version: number }, InfiniteData<ListPage>>(
    {
      mutationFn: ({ id, version }) =>
        api.teamDelete.mutation({
          params: { id },
          headers: { 'if-match': String(version) },
        }),
      // a csapat eltűnése a RÉSZLEG-sor `teams[]` listáját és a beosztás-sorok feloldott csapat-nevét is érinti
      invalidateKeys: [teamKeys.lists(), departmentKeys.lists(), positionKeys.lists()],
      optimistic: {
        queryKey: listKey,
        update: (old) => {
          if (!old) return old;
          const id = deleteTarget?.id;
          if (!id) return old;
          return {
            ...old,
            pages: old.pages.map((p) =>
              p.status === 200
                ? {
                    ...p,
                    body: {
                      ...p.body,
                      teams: p.body.teams.filter((x) => x.id !== id),
                      total: Math.max(0, p.body.total - 1),
                    },
                  }
                : p,
            ),
          };
        },
      },
      messages: {
        success: t('toast:org_structure.teams.deleted'),
        conflict: t('toast:org_structure.teams.inUse'),
        forbidden: t('toast:org_structure.teams.forbidden'),
        validation: t('toast:org_structure.teams.validation'),
        error: t('toast:org_structure.teams.error'),
      },
    },
  );

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate(
      { id: deleteTarget.id, version: deleteTarget.version },
      { onSettled: () => setDeleteTarget(null) },
    );
  }

  const emDash = <span className="text-muted-foreground">—</span>;

  // Egy tag megjelenítendő neve (locale-sorrend, mint a support-teams/munkatársak-oldal).
  const memberName = useCallback(
    (m: Member): string =>
      formatName(
        { givenName: m.givenName, familyName: m.familyName, displayName: m.displayName },
        i18n.language,
      ),
    [i18n.language],
  );

  const columns: DataTableColumn<TeamRow>[] = [
    {
      // 1. Csapat — a lokalizált név a csapat SAJÁT színes badge-eként (a szín itt él).
      // A RÉSZLEG NEVE ITT SZÁNDÉKOSAN NINCS: a hovatartozás a Részlegek tabon látszik (ott a részleg-sor
      // hozza a csapatait), itt pedig a részleg-facet szűkít rá — a cellába írva csak zajt adott.
      id: 'name',
      header: t('orgStructure.teams.columns.name'),
      sortable: true,
      cardLabel: t('orgStructure.teams.columns.name'),
      width: 'minmax(0,2.5fr)',
      multiline: true,
      cell: (tm) => {
        const name = isEn ? tm.nameEn : tm.nameHu;
        return (
          <TeamBadge name={name || tm.slug} color={tm.color} className="max-w-full truncate" />
        );
      },
    },
    {
      // 2. Vezető (#117) — az eszköz-igény jóváhagyásának tájékoztató-címzettje; hiányzónál figyelmeztetés
      id: 'lead',
      header: t('orgStructure.teams.columns.lead'),
      cardLabel: t('orgStructure.teams.columns.lead'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      cell: (tm) =>
        tm.lead ? (
          <PersonChip
            name={memberName(tm.lead)}
            avatarUrl={tm.lead.avatarUrl}
            accentColor={tm.lead.teamColor}
            size="sm"
            className="gap-1.5"
          />
        ) : (
          <span
            className="text-warning inline-flex items-center gap-1.5 text-sm"
            title={t('orgStructure.teams.noLeadHint')}
          >
            <ExclamationTriangleIcon className="size-4 shrink-0" aria-hidden />
            {t('orgStructure.teams.noLead')}
          </span>
        ),
    },
    {
      // 3. Beosztások — a csapathoz tartozó beosztások lokalizált neve; 3+ tételnél legördülő.
      id: 'positions',
      header: t('orgStructure.teams.columns.positions'),
      cardLabel: t('orgStructure.teams.columns.positions'),
      width: 'minmax(0,2fr)',
      multiline: true,
      cell: (tm) =>
        tm.positions.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell<{ id: string; name: string }>
            items={tm.positions}
            getKey={(pos) => pos.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('orgStructure.morePositions', { count: n })}
            triggerAriaLabel={t('orgStructure.morePositionsAria')}
            renderItem={(pos) => <span className="block min-w-0 truncate">{pos.name}</span>}
          />
        ),
    },
    {
      // 4. Munkatársak — a csapathoz tartozó belső munkatársak (a beosztásaikon át aggregálva): profilkép +
      // név; 3+ tagnál legördülő (ExpandableCell, a support-teams „Tagok" oszlop pontos designja).
      id: 'members',
      header: t('orgStructure.columns.employees'),
      cardLabel: t('orgStructure.columns.employees'),
      card: 'subtitle',
      width: 'minmax(0,2fr)',
      multiline: true,
      cell: (tm) =>
        tm.members.length === 0 ? (
          emDash
        ) : (
          <ExpandableCell<Member>
            items={tm.members}
            getKey={(m) => m.id}
            visible={2}
            threshold={3}
            moreLabel={(n) => t('orgStructure.moreMembers', { count: n })}
            triggerAriaLabel={t('orgStructure.moreMembersAria')}
            renderItem={(m) => {
              const name = memberName(m);
              return (
                <PersonChip
                  name={name}
                  avatarUrl={m.avatarUrl}
                  accentColor={m.teamColor}
                  size="sm"
                  className="gap-1.5"
                />
              );
            }}
          />
        ),
    },
  ];

  return (
    <AppShell
      // Mobilon a lebegő FAB (primaryAction) az AKTÍV fül (Csapatok) create-jét nyitja — csak manage-joggal.
      // Desktopon a toolbar ListNewButton-ja (lásd lent, `hidden md:block`).
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('orgStructure.teams.new'),
              onClick: () => setFormTarget({ team: null }),
            }
          : undefined
      }
      toolbar={
        <div>
          <div className="px-4 pt-2">
            <OrgStructureTabs value={tab} onValueChange={onTabChange} />
          </div>
          <ListToolbar
            search={{
              value: searchInput,
              onChange: setSearchInput,
              placeholder: t('common.searchPlaceholder'),
            }}
            filters={[
              {
                key: 'department',
                label: t('orgStructure.teams.filterDepartment'),
                value: departmentFilter,
                onChange: setDepartmentFilter,
                options: departmentOptions.map((d) => ({ value: d, label: d })),
                allValue: ALL,
              },
            ]}
            meta={
              infinite.isFetching && !infinite.isFetchingNextPage
                ? t('orgStructure.loading')
                : !isError && !infinite.isLoading
                  ? t('orgStructure.teams.countOf', { shown: filteredRows.length, total })
                  : ''
            }
            actions={
              // A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást.
              <div className="hidden md:block">
                <ListNewButton
                  label={t('orgStructure.teams.new')}
                  onClick={() => setFormTarget({ team: null })}
                  enabled={canManage}
                  disabledTooltip={t('common.noCreatePermission')}
                />
              </div>
            }
          />
        </div>
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<TeamRow>
          columns={columns}
          rows={filteredRows}
          getRowId={(tm) => tm.id}
          sort={sort}
          order={order}
          onSort={toggleSort}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('orgStructure.teams.noMatches')}
                description={t('orgStructure.teams.noMatchesDesc')}
              />
            ) : (
              <EmptyState
                title={t('orgStructure.teams.empty')}
                description={t('orgStructure.teams.emptyDesc')}
              />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('orgStructure.columns.actions')}
          actionsColWidth={canManage && canDelete ? '7rem' : canManage || canDelete ? '4rem' : '0'}
          rowActions={
            canManage || canDelete
              ? (tm) => (
                  <RowActions>
                    {canManage && (
                      <EditRowAction
                        label={t('orgStructure.edit')}
                        onClick={() => setFormTarget({ team: tm })}
                      />
                    )}
                    {canDelete && (
                      <DeleteRowAction
                        label={t('orgStructure.delete')}
                        onClick={() => setDeleteTarget(tm)}
                      />
                    )}
                  </RowActions>
                )
              : undefined
          }
        />
      </div>

      <TeamFormSheet
        open={formTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFormTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?edit` param törlése (back-barát + újra-nyitható)
          }
        }}
        team={formTarget?.team ?? null}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('orgStructure.teams.deleteTitle')}
        description={t('orgStructure.teams.deleteDesc', {
          name: deleteTarget ? (isEn ? deleteTarget.nameEn : deleteTarget.nameHu) : '',
        })}
        confirmLabel={t('orgStructure.delete')}
        cancelLabel={t('orgStructure.cancel')}
        pendingLabel={t('orgStructure.deleting')}
        tone="danger"
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
